import { useState } from "react";
import {
  DataTable,
  DataTableColumnHeader,
  Button,
  Tag,
  Checkbox,
  InputField,
  Textarea,
  RadioGroup,
  RadioGroupItem,
  Label,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DatePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Alert,
  AlertDescription,
  DismissibleTag,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  Paperclip,
  Pencil,
  Download,
  Trash2,
  Eye,
  Users,
  AlertCircle,
  Send,
  FileText,
  Filter,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  X,
  Calendar,
  Search,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface Segment {
  id: string;
  name: string;
  employeeCount: number;
}

interface AffiliatedCompany {
  id: string;
  name: string;
  employeeCount: number;
  segments: Segment[];
}

interface MultiMessage {
  id: string;
  title: string;
  category: string;
  companiesSent: number;
  totalRecipients: string;
  sentBy: string;
  sentDate: string;
  closeDate: string;
  hasAttachment: boolean;
  status: "LIVE" | "DRAFT" | "SCHEDULED" | "EXPIRED";
  template?: string;
  body?: string;
}

interface SegmentRule {
  id: string;
  field: string;
  operator: string;
  values: string[];
  valueType: "select" | "text" | "date";
}

const fieldOptions = [
  { value: "department", label: "Department" },
  { value: "job_title", label: "Job Title" },
  { value: "location", label: "Location" },
  { value: "employment_type", label: "Employment Type" },
  { value: "start_date", label: "Start Date" },
  { value: "salary_band", label: "Salary Band" },
];

const operatorOptions: Record<string, { value: string; label: string; valueType: SegmentRule["valueType"] }[]> = {
  department: [
    { value: "is", label: "Is", valueType: "select" },
    { value: "is_not", label: "Is not", valueType: "select" },
    { value: "contains", label: "Contains", valueType: "text" },
  ],
  job_title: [
    { value: "is", label: "Is", valueType: "select" },
    { value: "is_not", label: "Is not", valueType: "select" },
    { value: "contains", label: "Contains", valueType: "text" },
  ],
  location: [
    { value: "is", label: "Is", valueType: "select" },
    { value: "is_not", label: "Is not", valueType: "select" },
  ],
  employment_type: [
    { value: "is", label: "Is", valueType: "select" },
    { value: "is_not", label: "Is not", valueType: "select" },
  ],
  start_date: [
    { value: "between", label: "Between", valueType: "date" },
    { value: "before", label: "Before", valueType: "date" },
    { value: "after", label: "After", valueType: "date" },
  ],
  salary_band: [
    { value: "is", label: "Is", valueType: "select" },
    { value: "between", label: "Between", valueType: "text" },
  ],
};

function createEmptyRule(): SegmentRule {
  return { id: `rule-${Date.now()}-${Math.random()}`, field: "", operator: "", values: [], valueType: "select" };
}

/* ------------------------------------------------------------------ */
/*  Segment Builder Component                                         */
/* ------------------------------------------------------------------ */

function SegmentBuilder({
  onSave,
  onCancel,
  defaultName,
  defaultRules,
  saveLabel,
}: {
  onSave: (name: string, rules: SegmentRule[], estimatedCount: number) => void;
  onCancel: () => void;
  defaultName?: string;
  defaultRules?: SegmentRule[];
  saveLabel?: string;
}) {
  const [rules, setRules] = useState<SegmentRule[]>(defaultRules || [createEmptyRule()]);
  const [tagInput, setTagInput] = useState<Record<string, string>>({});

  // Mock estimated count based on number of rules with fields filled
  const filledRules = rules.filter((r) => r.field && r.operator);
  const estimatedCount = filledRules.length > 0 ? Math.max(12, 76 - (filledRules.length - 1) * 18) : 0;

  const addRule = () => {
    setRules((prev) => [...prev, createEmptyRule()]);
  };

  const removeRule = (id: string) => {
    if (rules.length <= 1) return;
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRule = (id: string, updates: Partial<SegmentRule>) => {
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  const addTagValue = (ruleId: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId && !r.values.includes(trimmed)
          ? { ...r, values: [...r.values, trimmed] }
          : r
      )
    );
    setTagInput((prev) => ({ ...prev, [ruleId]: "" }));
  };

  const removeTagValue = (ruleId: string, value: string) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? { ...r, values: r.values.filter((v) => v !== value) }
          : r
      )
    );
  };

  const [segmentName, setSegmentName] = useState(defaultName || "");

  return (
    <div className="border-t border-greyscale-border px-md py-sm bg-white">
      <div className="flex flex-col gap-sm">
        {/* Segment name */}
        <div className="flex flex-col gap-xxxs">
          <p className="text-sm font-semibold text-greyscale-text-title">
            Segment name <span className="text-red-500">*</span>
          </p>
          <InputField
            value={segmentName}
            onChange={(e) => setSegmentName(e.target.value)}
            placeholder="e.g. Managers"
          />
        </div>

        {rules.map((rule, index) => (
          <div key={rule.id}>
            {/* OR divider */}
            {index > 0 && (
              <div className="py-xs">
                <span className="text-sm font-semibold text-greyscale-text-title">OR</span>
              </div>
            )}

            {/* Inputs row with labels */}
            <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-xs items-end">
              {/* Field */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Field <span className="text-red-500">*</span>
                </span>
                <Select
                  value={rule.field}
                  onValueChange={(val) => {
                    updateRule(rule.id, { field: val, operator: "", values: [], valueType: "select" });
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Rule */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Rule <span className="text-red-500">*</span>
                </span>
                <Select
                  value={rule.operator}
                  onValueChange={(val) => {
                    const ops = operatorOptions[rule.field] || [];
                    const selectedOp = ops.find((o) => o.value === val);
                    updateRule(rule.id, {
                      operator: val,
                      valueType: selectedOp?.valueType || "select",
                      values: [],
                    });
                  }}
                  disabled={!rule.field}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select rule" />
                  </SelectTrigger>
                  <SelectContent>
                    {(operatorOptions[rule.field] || []).map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Value */}
              <div className="flex flex-col gap-xxxs">
                <span className="text-sm font-semibold text-greyscale-text-title">
                  Value <span className="text-red-500">*</span>
                </span>
                {rule.valueType === "select" ? (
                  <div className="flex items-center gap-xxs border border-greyscale-border rounded-lg px-xs h-[46px] flex-wrap overflow-hidden">
                    {rule.values.map((v) => (
                      <DismissibleTag
                        key={v}
                        variant="navy"
                        onDismiss={() => removeTagValue(rule.id, v)}
                      >
                        {v}
                      </DismissibleTag>
                    ))}
                    <input
                      value={tagInput[rule.id] || ""}
                      onChange={(e) => setTagInput((prev) => ({ ...prev, [rule.id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTagValue(rule.id, tagInput[rule.id] || "");
                        }
                      }}
                      placeholder={rule.values.length === 0 ? "Type and press Enter" : ""}
                      disabled={!rule.operator}
                      className="flex-1 min-w-[100px] text-sm outline-none bg-transparent"
                    />
                  </div>
                ) : rule.valueType === "text" ? (
                  <InputField
                    value={rule.values[0] || ""}
                    onChange={(e) => updateRule(rule.id, { values: [e.target.value] })}
                    placeholder="Open text field"
                    disabled={!rule.operator}
                  />
                ) : (
                  <div className="flex items-center gap-xs">
                    <DatePicker
                      date={rule.values[0] ? new Date(rule.values[0]) : undefined}
                      onDateChange={(d) =>
                        updateRule(rule.id, {
                          values: [d ? d.toISOString().split("T")[0] : "", rule.values[1] || ""],
                        })
                      }
                      placeholder="Start date"
                      disabled={!rule.operator}
                      className="flex-1"
                    />
                    <DatePicker
                      date={rule.values[1] ? new Date(rule.values[1]) : undefined}
                      onDateChange={(d) =>
                        updateRule(rule.id, {
                          values: [rule.values[0] || "", d ? d.toISOString().split("T")[0] : ""],
                        })
                      }
                      placeholder="End date"
                      disabled={!rule.operator}
                      className="flex-1"
                    />
                  </div>
                )}
              </div>

              {/* Add / Remove buttons */}
              <div className="flex items-center gap-xxs self-end mb-[1px]">
                <Button size="sm" onClick={addRule}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeRule(rule.id)}
                  disabled={rules.length <= 1}
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        ))}

        {/* Estimated match banner */}
        {estimatedCount > 0 && (
          <div className="flex items-center gap-xs px-md py-sm bg-[#FFF4ED] rounded-md border border-[#FDDCBB] mt-xs">
            <AlertCircle size={18} className="text-[#E5722E]" />
            <span className="text-sm font-semibold text-[#E5722E]">
              Estimated match: {estimatedCount} Employees
            </span>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex items-center justify-end gap-xs pt-xs">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave(segmentName || `Segment ${Date.now()}`, rules, estimatedCount);
            }}
          >
            {saveLabel || "Save Segment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock data                                                         */
/* ------------------------------------------------------------------ */

const initialCompanies: AffiliatedCompany[] = [
  {
    id: "c1",
    name: "KFC Franchise – Gauteng",
    employeeCount: 342,
    segments: [
      { id: "s-managers", name: "Managers", employeeCount: 24 },
      { id: "s-shift-leads", name: "Shift Leads", employeeCount: 48 },
      { id: "s-kitchen", name: "Kitchen Staff", employeeCount: 128 },
      { id: "s-front", name: "Front of House", employeeCount: 96 },
      { id: "s-drivers", name: "Delivery Drivers", employeeCount: 34 },
      { id: "s-admin", name: "Admin & Support", employeeCount: 12 },
    ],
  },
  {
    id: "c2",
    name: "KFC Franchise – Western Cape",
    employeeCount: 215,
    segments: [
      { id: "s-managers", name: "Managers", employeeCount: 16 },
      { id: "s-shift-leads", name: "Shift Leads", employeeCount: 30 },
      { id: "s-kitchen", name: "Kitchen Staff", employeeCount: 82 },
      { id: "s-front", name: "Front of House", employeeCount: 58 },
      { id: "s-drivers", name: "Delivery Drivers", employeeCount: 21 },
      { id: "s-admin", name: "Admin & Support", employeeCount: 8 },
    ],
  },
  {
    id: "c3",
    name: "KFC Franchise – KwaZulu-Natal",
    employeeCount: 189,
    segments: [
      { id: "s-managers", name: "Managers", employeeCount: 14 },
      { id: "s-shift-leads", name: "Shift Leads", employeeCount: 26 },
      { id: "s-kitchen", name: "Kitchen Staff", employeeCount: 72 },
      { id: "s-front", name: "Front of House", employeeCount: 51 },
      { id: "s-drivers", name: "Delivery Drivers", employeeCount: 18 },
      { id: "s-admin", name: "Admin & Support", employeeCount: 8 },
    ],
  },
  {
    id: "c4",
    name: "KFC Franchise – Eastern Cape",
    employeeCount: 127,
    segments: [
      { id: "s-managers", name: "Managers", employeeCount: 9 },
      { id: "s-shift-leads", name: "Shift Leads", employeeCount: 18 },
      { id: "s-kitchen", name: "Kitchen Staff", employeeCount: 48 },
      { id: "s-front", name: "Front of House", employeeCount: 34 },
      { id: "s-drivers", name: "Delivery Drivers", employeeCount: 12 },
      { id: "s-admin", name: "Admin & Support", employeeCount: 6 },
    ],
  },
  {
    id: "c5",
    name: "KFC Franchise – Free State",
    employeeCount: 98,
    segments: [
      { id: "s-managers", name: "Managers", employeeCount: 7 },
      { id: "s-shift-leads", name: "Shift Leads", employeeCount: 14 },
      { id: "s-kitchen", name: "Kitchen Staff", employeeCount: 38 },
      { id: "s-front", name: "Front of House", employeeCount: 26 },
      { id: "s-drivers", name: "Delivery Drivers", employeeCount: 9 },
      { id: "s-admin", name: "Admin & Support", employeeCount: 4 },
    ],
  },
];

const initialMessages: MultiMessage[] = [
  {
    id: "1",
    title: "Q1 Operational Update",
    category: "Operational Update",
    companiesSent: 5,
    totalRecipients: "971/971",
    sentBy: "Sarah Johnson",
    sentDate: "08/03/2026 09:00",
    closeDate: "15/03/2026 09:00",
    hasAttachment: true,
    status: "LIVE",
    template: "operational-update",
    body: "Please be advised of the following operational updates for Q1 2026. All franchise locations are expected to comply with the revised procedures effective immediately.",
  },
  {
    id: "2",
    title: "New Uniform Policy – Effective April",
    category: "Policy Notice",
    companiesSent: 5,
    totalRecipients: "971/971",
    sentBy: "Sarah Johnson",
    sentDate: "05/03/2026 14:30",
    closeDate: "12/03/2026 14:30",
    hasAttachment: true,
    status: "LIVE",
    template: "policy-notice",
    body: "A new uniform policy will take effect from 1 April 2026. All staff members must ensure compliance with the updated dress code. Please review the attached document for full details.",
  },
  {
    id: "3",
    title: "Easter Trading Hours",
    category: "Operational Update",
    companiesSent: 3,
    totalRecipients: "746/746",
    sentBy: "Mark Peters",
    sentDate: "01/03/2026 08:00",
    closeDate: "05/04/2026 08:00",
    hasAttachment: false,
    status: "SCHEDULED",
    template: "operational-update",
    body: "Please take note of the adjusted trading hours over the Easter period. Managers should ensure rosters are updated accordingly.",
  },
  {
    id: "4",
    title: "Health & Safety Reminder",
    category: "Policy Notice",
    companiesSent: 5,
    totalRecipients: "971/971",
    sentBy: "Sarah Johnson",
    sentDate: "25/02/2026 10:15",
    closeDate: "04/03/2026 10:15",
    hasAttachment: false,
    status: "EXPIRED",
    template: "policy-notice",
    body: "This is a reminder to all staff regarding health and safety protocols. Please ensure that all safety equipment is inspected and that incident reports are submitted on time.",
  },
  {
    id: "5",
    title: "Regional Manager Recruitment",
    category: "Recruitment",
    companiesSent: 2,
    totalRecipients: "0/557",
    sentBy: "Sarah Johnson",
    sentDate: "",
    closeDate: "",
    hasAttachment: false,
    status: "DRAFT",
    template: "urgent-management",
    body: "We are looking for a Regional Manager to oversee operations across the Gauteng and Western Cape franchises. Interested candidates should apply by end of March.",
  },
  {
    id: "6",
    title: "February Payslip Notice",
    category: "General",
    companiesSent: 5,
    totalRecipients: "968/971",
    sentBy: "Mark Peters",
    sentDate: "20/02/2026 07:00",
    closeDate: "27/02/2026 07:00",
    hasAttachment: true,
    status: "EXPIRED",
    template: "general-notice",
    body: "Your February 2026 payslips are now available. Please log in to the employee portal to view and download your payslip. Contact HR if you have any queries.",
  },
];

const auditLog = [
  { date: "08/03/2026 09:00", user: "Sarah Johnson", activity: "Created announcement", name: "March Company Update", companies: 5 },
  { date: "08/03/2026 08:55", user: "Sarah Johnson", activity: "Updated segment", name: "Gauteng Region", companies: 3 },
  { date: "08/03/2026 08:50", user: "Sarah Johnson", activity: "Created announcement", name: "Policy Change Notice", companies: 4 },
  { date: "05/03/2026 14:30", user: "Mark Peters", activity: "Updated segment", name: "Western Cape Staff", companies: 2 },
  { date: "01/03/2026 08:00", user: "Mark Peters", activity: "Created announcement", name: "Benefits Enrollment", companies: 6 },
  { date: "25/02/2026 10:15", user: "Sarah Johnson", activity: "Deleted segment", name: "Old Team List", companies: 1 },
  { date: "20/02/2026 16:42", user: "Sarah Johnson", activity: "Created announcement", name: "Holiday Schedule", companies: 3 },
  { date: "15/02/2026 11:30", user: "Mark Peters", activity: "Updated segment", name: "KZN Operations", companies: 5 },
];

type AuditEntry = (typeof auditLog)[number];

const auditColumns: ColumnDef<AuditEntry>[] = [
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-caption">{row.original.date}</span>
    ),
  },
  {
    accessorKey: "user",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="User" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-greyscale-text-title">{row.original.user}</span>
    ),
  },
  {
    accessorKey: "activity",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Activity" />
    ),
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Segment / Announcement Name" />
    ),
  },
  {
    accessorKey: "companies",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Companies" />
    ),
  },
];

const employeeColumns: ColumnDef<{ surname: string; firstName: string }>[] = [
  {
    accessorKey: "surname",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Surname" />
    ),
  },
  {
    accessorKey: "firstName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="First name" />
    ),
  },
];

/* ------------------------------------------------------------------ */
/*  Table columns                                                     */
/* ------------------------------------------------------------------ */

const getColumns = (onView: (m: MultiMessage) => void, onEdit: (m: MultiMessage) => void, onDelete: (m: MultiMessage) => void): ColumnDef<MultiMessage>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(v) => row.toggleSelected(!!v)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Message" />
    ),
    cell: ({ row }) => (
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">
          {row.original.title}
        </p>
        <p className="text-xs text-greyscale-text-caption">
          {row.original.category}
        </p>
      </div>
    ),
  },
  {
    accessorKey: "companiesSent",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Companies" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">{row.original.companiesSent}</span>
    ),
  },
  {
    accessorKey: "totalRecipients",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Recipients" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-xxs">
        <Users size={14} className="text-greyscale-text-caption" />
        <span className="text-sm">{row.original.totalRecipients}</span>
      </div>
    ),
  },
  {
    accessorKey: "sentBy",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sent By" />
    ),
  },
  {
    accessorKey: "sentDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sent Date" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">
        {row.original.sentDate || "—"}
      </span>
    ),
  },
  {
    accessorKey: "hasAttachment",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Attachments" />
    ),
    cell: ({ row }) => (
      <Paperclip
        size={16}
        className={
          row.original.hasAttachment
            ? "text-greyscale-text-caption"
            : "text-greyscale-border"
        }
      />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const statusMap: Record<string, string> = {
        LIVE: "success",
        DRAFT: "drafted",
        SCHEDULED: "processing",
        EXPIRED: "failed",
      };
      return (
        <Tag variant={statusMap[row.original.status] as any}>
          {row.original.status}
        </Tag>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-xxxs justify-start">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-greyscale-text-caption hover:text-greyscale-text-title h-8 w-8"
                onClick={() => onView(row.original)}
              >
                <Eye size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="dark">View</TooltipContent>
          </Tooltip>
          {row.original.status === "DRAFT" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-greyscale-text-caption hover:text-greyscale-text-title h-8 w-8"
                  onClick={() => onEdit(row.original)}
                >
                  <Pencil size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="dark">Edit</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-greyscale-text-caption hover:text-greyscale-text-title h-8 w-8"
              >
                <Download size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Download</TooltipContent>
          </Tooltip>
          {row.original.status === "DRAFT" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-greyscale-text-caption hover:text-red-500 h-8 w-8"
                  onClick={() => onDelete(row.original)}
                >
                  <Trash2 size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="dark">Delete</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    ),
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

type View = "list" | "compose" | "audit" | "segments" | "view" | "create-segment" | "edit-segment" | "view-segment" | "segment-employees";

// Mock employee data per segment
const mockEmployeeNames: Record<string, { surname: string; firstName: string }[]> = {
  "s-managers": [
    { surname: "Michaels", firstName: "Abigail" },
    { surname: "Makaula", firstName: "Abongile" },
    { surname: "Dlamini", firstName: "Adam" },
    { surname: "Kling", firstName: "Alana" },
    { surname: "Kuhlman", firstName: "Alene" },
    { surname: "Ngongoma", firstName: "Andile" },
    { surname: "Gumede", firstName: "Bhekani" },
    { surname: "Lifschitz", firstName: "Danielle" },
    { surname: "Mthembu", firstName: "Sipho" },
    { surname: "Van der Merwe", firstName: "Johan" },
    { surname: "Naidoo", firstName: "Priya" },
    { surname: "Botha", firstName: "Riaan" },
  ],
  "s-shift-leads": [
    { surname: "Zulu", firstName: "Themba" },
    { surname: "Pillay", firstName: "Kavitha" },
    { surname: "Mokoena", firstName: "Lerato" },
    { surname: "Jacobs", firstName: "Michael" },
    { surname: "Ndlovu", firstName: "Nompilo" },
    { surname: "September", firstName: "Craig" },
    { surname: "Molefe", firstName: "Thabiso" },
    { surname: "Pretorius", firstName: "Annika" },
  ],
  "s-kitchen": [
    { surname: "Khumalo", firstName: "Zanele" },
    { surname: "Mbeki", firstName: "Thabo" },
    { surname: "Govender", firstName: "Suren" },
    { surname: "Sithole", firstName: "Nomsa" },
    { surname: "De Villiers", firstName: "Pieter" },
    { surname: "Mahlangu", firstName: "Busi" },
    { surname: "Tshabalala", firstName: "Lucky" },
    { surname: "Naicker", firstName: "Devi" },
    { surname: "Mkhize", firstName: "Sifiso" },
    { surname: "Fourie", firstName: "Elna" },
  ],
  "s-front": [
    { surname: "Radebe", firstName: "Lindiwe" },
    { surname: "Smit", firstName: "Gerhard" },
    { surname: "Cele", firstName: "Ayanda" },
    { surname: "Abrahams", firstName: "Fatima" },
    { surname: "Mabaso", firstName: "Thulani" },
    { surname: "Joubert", firstName: "Chantal" },
    { surname: "Maseko", firstName: "Sibusiso" },
    { surname: "Chetty", firstName: "Kamini" },
  ],
  "s-drivers": [
    { surname: "Zwane", firstName: "Mandla" },
    { surname: "Erasmus", firstName: "Hennie" },
    { surname: "Buthelezi", firstName: "Nhlanhla" },
    { surname: "Patel", firstName: "Rajan" },
    { surname: "Shabangu", firstName: "Vusi" },
    { surname: "Le Roux", firstName: "Francois" },
  ],
  "s-admin": [
    { surname: "Motaung", firstName: "Palesa" },
    { surname: "Wessels", firstName: "Karen" },
    { surname: "Dube", firstName: "Nokuthula" },
    { surname: "Meyer", firstName: "Christa" },
  ],
};

// Segments that are "in use" (used in an active announcement)
const segmentsInUse = new Set(["s-managers", "s-shift-leads", "s-kitchen", "s-front"]);

// Mock company names per message
const messageCompanies: Record<string, string[]> = {
  "1": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape", "KFC Franchise – KwaZulu-Natal", "KFC Franchise – Eastern Cape", "KFC Franchise – Free State"],
  "2": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape", "KFC Franchise – KwaZulu-Natal", "KFC Franchise – Eastern Cape", "KFC Franchise – Free State"],
  "3": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape", "KFC Franchise – KwaZulu-Natal"],
  "4": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape", "KFC Franchise – KwaZulu-Natal", "KFC Franchise – Eastern Cape", "KFC Franchise – Free State"],
  "5": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape"],
  "6": ["KFC Franchise – Gauteng", "KFC Franchise – Western Cape", "KFC Franchise – KwaZulu-Natal", "KFC Franchise – Eastern Cape", "KFC Franchise – Free State"],
};

// Mock draft selections (company IDs and segment keys) for pre-filling edit
const draftSelections: Record<string, { companyIds: string[]; segmentKeys: string[] }> = {
  "5": {
    companyIds: ["kfc-gp", "kfc-wc"],
    segmentKeys: ["kfc-gp:s-managers", "kfc-gp:s-shift-leads", "kfc-wc:s-managers"],
  },
};

export function Multimessaging() {
  const [view, setView] = useState<View>("list");
  const [viewingMessage, setViewingMessage] = useState<MultiMessage | null>(null);
  const [companies, setCompanies] = useState<AffiliatedCompany[]>(initialCompanies);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  // segmentMode: "all" = all employees, "segments" = filtered by segment
  const [segmentMode, setSegmentMode] = useState<"all" | "segments">("all");
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);

  // Segment management state
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [newSegmentName, setNewSegmentName] = useState("");
  const [newSegmentCount, setNewSegmentCount] = useState("");
  const [createSegmentCompanyId, setCreateSegmentCompanyId] = useState<string>("");
  const [segmentFilterCompany, setSegmentFilterCompany] = useState<string>("all");
  const [viewingSegment, setViewingSegment] = useState<{ company: AffiliatedCompany; segment: Segment } | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [editingSegment, setEditingSegment] = useState<{ companyId: string; segmentId: string; name: string; count: string } | null>(null);
  const [dateRangeOption, setDateRangeOption] = useState("7-days");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState("12:09");
  const [endTime, setEndTime] = useState("12:09");

  const [messageList, setMessageList] = useState<MultiMessage[]>(initialMessages);
  const [editingMessage, setEditingMessage] = useState<MultiMessage | null>(null);
  const [deletingMessage, setDeletingMessage] = useState<MultiMessage | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const columns = getColumns(
    (m) => {
      setViewingMessage(m);
      setView("view");
    },
    (m) => {
      setEditingMessage(m);
      const draft = draftSelections[m.id];
      if (draft) {
        setSelectedCompanies(draft.companyIds);
        setSelectedSegments(draft.segmentKeys);
      }
      setView("compose");
    },
    (m) => {
      setDeletingMessage(m);
    }
  );

  // Derive all unique segments across companies for the segment picker
  const allSegments = (() => {
    const map = new Map<string, string>();
    companies.forEach((c) => c.segments.forEach((s) => map.set(s.id, s.name)));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  })();

  const addSegment = (companyId: string) => {
    const name = newSegmentName.trim();
    const count = parseInt(newSegmentCount, 10);
    if (!name || isNaN(count) || count < 0) return;

    const segId = `s-${name.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? { ...c, segments: [...c.segments, { id: segId, name, employeeCount: count }] }
          : c
      )
    );
    setNewSegmentName("");
    setNewSegmentCount("");
    setEditingCompanyId(null);
  };

  const deleteSegment = (companyId: string, segmentId: string) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? { ...c, segments: c.segments.filter((s) => s.id !== segmentId) }
          : c
      )
    );
    setSelectedSegments((prev) => prev.filter((id) => id !== segmentId));
  };

  const saveEditSegment = () => {
    if (!editingSegment) return;
    const name = editingSegment.name.trim();
    const count = parseInt(editingSegment.count, 10);
    if (!name || isNaN(count) || count < 0) return;

    setCompanies((prev) =>
      prev.map((c) =>
        c.id === editingSegment.companyId
          ? {
              ...c,
              segments: c.segments.map((s) =>
                s.id === editingSegment.segmentId
                  ? { ...s, name, employeeCount: count }
                  : s
              ),
            }
          : c
      )
    );
    setEditingSegment(null);
  };

  const toggleCompany = (id: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map((c) => c.id));
    }
  };

  const toggleSegment = (segmentId: string) => {
    setSelectedSegments((prev) =>
      prev.includes(segmentId)
        ? prev.filter((s) => s !== segmentId)
        : [...prev, segmentId]
    );
  };

  const selectAllSegments = () => {
    if (selectedSegments.length === allSegments.length) {
      setSelectedSegments([]);
    } else {
      setSelectedSegments(allSegments.map((s) => s.id));
    }
  };

  // Calculate recipients from selected segments (companyId:segmentId keys)
  const getCompanyRecipients = (company: AffiliatedCompany) => {
    const companySegs = selectedSegments.filter((k) => k.startsWith(`${company.id}:`));
    if (companySegs.length === 0) return 0;
    return company.segments
      .filter((s) => companySegs.includes(`${company.id}:${s.id}`))
      .reduce((sum, s) => sum + s.employeeCount, 0);
  };

  const totalRecipients = companies
    .filter((c) => selectedCompanies.includes(c.id))
    .reduce((sum, c) => sum + getCompanyRecipients(c), 0);

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Multimessaging
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Send announcements across affiliated company profiles in a single
            action.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          {view === "list" && (
            <>
              <Button
                variant="outline"
                leftIcon={<Filter size={16} />}
                onClick={() => setView("segments")}
              >
                Manage Segments
              </Button>
              <Button
                variant="outline"
                leftIcon={<FileText size={16} />}
                onClick={() => setView("audit")}
              >
                History Log
              </Button>
              <Button
                leftIcon={<Plus size={16} />}
                onClick={() => setView("compose")}
              >
                Create New
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ---------- LIST VIEW ---------- */}
      {view === "list" && (
        <DataTable
          columns={columns}
          data={messageList}
          showPagination
          showToolbar={false}
        />
      )}

      {/* ---------- COMPOSE VIEW ---------- */}
      {view === "compose" && (
        <div>
          <div className="max-w-[850px]">
            <p className="text-base font-semibold text-greyscale-text-title mb-md">
              {editingMessage ? "Edit draft announcement" : "Compose cross-company announcement"}
            </p>

            <div className="flex flex-col gap-md">
              {/* Select a template */}
              <div className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Select a template
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    Choose a message template to get started
                  </p>
                </div>
                <Select defaultValue={editingMessage?.template || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disciplinary-notice">Disciplinary Notice</SelectItem>
                    <SelectItem value="training-development">Training & Development</SelectItem>
                    <SelectItem value="urgent-management">Urgent Management</SelectItem>
                    <SelectItem value="contact">Contact</SelectItem>
                    <SelectItem value="language-specific">Language Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Title
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    Give your announcement a clear, descriptive title.
                  </p>
                </div>
                <InputField
                  defaultValue={editingMessage?.title || ""}
                  placeholder="e.g. Q1 Operational Update"
                />
              </div>

              {/* Message */}
              <div className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Message
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    Write the announcement body. This will be seen by all
                    employees in the selected companies.
                  </p>
                </div>
                <Textarea
                  defaultValue={editingMessage?.body || ""}
                  placeholder="Write your announcement here..."
                  className="min-h-[115px]"
                />
              </div>

              {/* Attach media */}
              <Button
                leftIcon={<Paperclip size={11} />}
                className="w-fit"
              >
                Attach Media
              </Button>

              {/* Date range */}
              <div className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Visibility window
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    How long should this announcement be visible to employees?
                  </p>
                </div>
                <RadioGroup
                  value={dateRangeOption}
                  onValueChange={setDateRangeOption}
                  className="flex gap-xs"
                >
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="24h" id="24h" />
                    <Label htmlFor="24h" className="text-sm">24h</Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="3-days" id="3-days" />
                    <Label htmlFor="3-days" className="text-sm">3 days</Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="7-days" id="7-days" />
                    <Label htmlFor="7-days" className="text-sm">7 days</Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="custom" id="custom" />
                    <Label htmlFor="custom" className="text-sm">Custom</Label>
                  </div>
                </RadioGroup>

                {dateRangeOption === "custom" && (
                  <div className="grid grid-cols-2 gap-md mt-xs">
                    <div className="flex flex-col gap-xxxs">
                      <Label className="text-sm font-semibold text-greyscale-text-title">
                        Start Date <span className="text-red-500">*</span>
                      </Label>
                      <DatePicker
                        date={startDate}
                        onDateChange={setStartDate}
                        placeholder="Select start date"
                      />
                    </div>
                    <div className="flex flex-col gap-xxxs">
                      <Label className="text-sm font-semibold text-greyscale-text-title">
                        Start Time
                      </Label>
                      <InputField
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        placeholder="HH : MM"
                      />
                    </div>
                    <div className="flex flex-col gap-xxxs">
                      <Label className="text-sm font-semibold text-greyscale-text-title">
                        End Date <span className="text-red-500">*</span>
                      </Label>
                      <DatePicker
                        date={endDate}
                        onDateChange={setEndDate}
                        placeholder="Select end date"
                      />
                    </div>
                    <div className="flex flex-col gap-xxxs">
                      <Label className="text-sm font-semibold text-greyscale-text-title">
                        End Time
                      </Label>
                      <InputField
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        placeholder="HH : MM"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Select companies & segments */}
              <div className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Select companies & segments
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    Choose which companies and segments should receive this
                    announcement. Expand a company to pick specific segments.
                  </p>
                </div>

                <div className="border border-greyscale-border rounded-lg overflow-hidden">
                  {/* Company rows */}
                  {companies.map((company) => {
                    const isExpanded = expandedCompany === company.id;
                    const companySegKeys = company.segments.map(
                      (s) => `${company.id}:${s.id}`
                    );
                    const selectedCount = companySegKeys.filter((k) =>
                      selectedSegments.includes(k)
                    ).length;
                    const allSelected = selectedCount === company.segments.length && company.segments.length > 0;
                    const someSelected = selectedCount > 0 && !allSelected;
                    const segEmployees = company.segments
                      .filter((s) => selectedSegments.includes(`${company.id}:${s.id}`))
                      .reduce((sum, s) => sum + s.employeeCount, 0);

                    return (
                      <div key={company.id} className="border-b last:border-b-0 border-greyscale-border">
                        <div
                          className="flex items-center justify-between px-md py-xs hover:bg-greyscale-bg-subtle/50 cursor-pointer"
                          onClick={() =>
                            setExpandedCompany(isExpanded ? null : company.id)
                          }
                        >
                          <div className="flex items-center gap-xxs">
                            <Checkbox
                              checked={allSelected}
                              ref={(el) => {
                                if (el) {
                                  const input = el as unknown as HTMLButtonElement;
                                  input.dataset.indeterminate = someSelected ? "true" : "false";
                                }
                              }}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCompanies((prev) =>
                                    prev.includes(company.id) ? prev : [...prev, company.id]
                                  );
                                  setSelectedSegments((prev) => [
                                    ...prev.filter((k) => !k.startsWith(`${company.id}:`)),
                                    ...companySegKeys,
                                  ]);
                                } else {
                                  setSelectedCompanies((prev) =>
                                    prev.filter((id) => id !== company.id)
                                  );
                                  setSelectedSegments((prev) =>
                                    prev.filter((k) => !k.startsWith(`${company.id}:`))
                                  );
                                }
                              }}
                              aria-label={`Select ${company.name}`}
                            />
                            <span className="text-sm text-greyscale-text-title">
                              {company.name}
                            </span>
                            {selectedCount > 0 && (
                              <Tag variant="info">
                                {selectedCount}/{company.segments.length} segments
                              </Tag>
                            )}
                          </div>
                          <div className="flex items-center gap-sm">
                            <span className="text-sm text-greyscale-text-caption">
                              {selectedCount > 0
                                ? `${segEmployees} / ${company.employeeCount}`
                                : company.employeeCount}{" "}
                              employees
                            </span>
                            {isExpanded ? (
                              <ChevronUp size={14} className="text-greyscale-text-caption" />
                            ) : (
                              <ChevronDown size={14} className="text-greyscale-text-caption" />
                            )}
                          </div>
                        </div>

                        {/* Expanded: segment checkboxes */}
                        {isExpanded && (
                          <div className="border-t border-greyscale-border bg-greyscale-bg-subtle/30">
                            {company.segments.map((seg) => {
                              const segKey = `${company.id}:${seg.id}`;
                              return (
                                <div
                                  key={seg.id}
                                  className="flex items-center pl-xl pr-md py-xxs hover:bg-greyscale-bg-subtle/50 cursor-pointer border-b last:border-b-0 border-greyscale-border/50"
                                  onClick={() => {
                                    const isSelected = selectedSegments.includes(segKey);
                                    if (isSelected) {
                                      setSelectedSegments((prev) =>
                                        prev.filter((k) => k !== segKey)
                                      );
                                      const remaining = selectedSegments.filter(
                                        (k) => k.startsWith(`${company.id}:`) && k !== segKey
                                      );
                                      if (remaining.length === 0) {
                                        setSelectedCompanies((prev) =>
                                          prev.filter((id) => id !== company.id)
                                        );
                                      }
                                    } else {
                                      setSelectedSegments((prev) => [...prev, segKey]);
                                      if (!selectedCompanies.includes(company.id)) {
                                        setSelectedCompanies((prev) => [...prev, company.id]);
                                      }
                                    }
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedSegments.includes(segKey)}
                                    onCheckedChange={() => {}}
                                    aria-label={`Select ${seg.name}`}
                                  />
                                  <span className="text-sm text-greyscale-text-body ml-xxs">
                                    {seg.name} ({seg.employeeCount})
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected segment tags */}
                {selectedSegments.length > 0 && (
                  <div className="flex flex-wrap gap-xxs mt-xxxs">
                    {selectedSegments.map((segKey) => {
                      const [compId, segId] = segKey.split(":");
                      const comp = companies.find((c) => c.id === compId);
                      const seg = comp?.segments.find((s) => s.id === segId);
                      if (!comp || !seg) return null;
                      return (
                        <DismissibleTag
                          key={segKey}
                          variant="pink"
                          onDismiss={() => {
                            setSelectedSegments((prev) =>
                              prev.filter((k) => k !== segKey)
                            );
                            const remaining = selectedSegments.filter(
                              (k) => k.startsWith(`${compId}:`) && k !== segKey
                            );
                            if (remaining.length === 0) {
                              setSelectedCompanies((prev) =>
                                prev.filter((id) => id !== compId)
                              );
                            }
                          }}
                        >
                          {comp.name} – {seg.name}
                        </DismissibleTag>
                      );
                    })}
                  </div>
                )}

                {/* Scope summary */}
                {selectedSegments.length > 0 && (
                  <Alert variant="warning" className="mt-xxxs">
                    <AlertDescription>
                      This message will be sent to{" "}
                      <strong>{totalRecipients} employees</strong> across{" "}
                      <strong>{selectedCompanies.length} companies</strong>
                      {" "}in{" "}
                      <strong>
                        {selectedSegments.length}{" "}
                        {selectedSegments.length === 1 ? "segment" : "segments"}
                      </strong>
                      .
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="border-t border-greyscale-border mt-lg" />
          <div className="flex items-center justify-between py-lg">
            <Button
              variant="outline"
              onClick={() => {
                setView("list");
                setEditingMessage(null);
                setSelectedCompanies([]);
                setSegmentMode("all");
                setSelectedSegments([]);
                setExpandedCompany(null);
              }}
            >
              Go Back
            </Button>
            <div className="flex items-center gap-xxs">
              {editingMessage && editingMessage.status === "LIVE" && (
                <Button
                  variant="outline"
                  className="text-red-500 border-red-500 hover:bg-red-50"
                  onClick={() => setShowCloseModal(true)}
                >
                  Close Announcement
                </Button>
              )}
              <Button variant="outline">Save draft</Button>
              <Button
                leftIcon={<Send size={14} />}
                disabled={selectedCompanies.length === 0}
              >
                Send to {totalRecipients} employees across{" "}
                {selectedCompanies.length}{" "}
                {selectedCompanies.length === 1 ? "company" : "companies"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- MANAGE SEGMENTS OVERVIEW ---------- */}
      {view === "segments" && (
        <div>
          <div className="flex items-center justify-between mb-lg">
            <div>
              <p className="text-base font-semibold text-greyscale-text-title">
                Manage Segments
              </p>
              <p className="text-sm text-greyscale-text-body mt-xxxs">
                Create and manage employee segments for each company.
              </p>
            </div>
            <div className="flex items-center gap-xxs">
              <Button
                leftIcon={<Plus size={16} />}
                onClick={() => {
                  setCreateSegmentCompanyId("");
                  setView("create-segment");
                }}
              >
                Create New Segment
              </Button>
              <Button variant="outline" onClick={() => setView("list")}>
                Back to Messages
              </Button>
            </div>
          </div>

          {/* Company filter */}
          <div className="mb-sm">
            <Select value={segmentFilterCompany} onValueChange={setSegmentFilterCompany}>
              <SelectTrigger className="w-full max-w-[300px]">
                <SelectValue placeholder="Filter by company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-xxs mb-xl">
            {companies
              .filter((c) => segmentFilterCompany === "all" || c.id === segmentFilterCompany)
              .map((company) => {
              const isExpanded = expandedCompany === company.id;
              return (
                <div
                  key={company.id}
                  className="border border-greyscale-border rounded-lg overflow-hidden"
                >
                  {/* Company header */}
                  <Button
                    variant="ghost"
                    className="flex items-center justify-between w-full px-md py-sm bg-greyscale-bg-subtle hover:bg-greyscale-bg-subtle/80 text-left rounded-none h-auto"
                    onClick={() =>
                      setExpandedCompany(isExpanded ? null : company.id)
                    }
                  >
                    <div className="flex items-center gap-xxs">
                      <span className="text-sm font-semibold text-greyscale-text-title">
                        {company.name}
                      </span>
                      <Tag variant="info">{company.segments.length} segments</Tag>
                    </div>
                    <div className="flex items-center gap-xxs">
                      <span className="text-sm text-greyscale-text-caption">
                        {company.employeeCount} employees
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={16} className="text-greyscale-text-caption" />
                      ) : (
                        <ChevronDown size={16} className="text-greyscale-text-caption" />
                      )}
                    </div>
                  </Button>

                  {/* Expanded: segment list */}
                  {isExpanded && (
                    <div>
                      {/* Segment table header */}
                      {company.segments.length > 0 && (
                        <div className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs border-t border-greyscale-border bg-white">
                          <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                            Segment Name
                          </span>
                          <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                            Employees
                          </span>
                          <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                            Status
                          </span>
                          <span className="text-xs font-semibold text-greyscale-text-caption uppercase">
                            Actions
                          </span>
                        </div>
                      )}

                      {/* Segment rows */}
                      {company.segments.map((seg) => {
                        const inUse = segmentsInUse.has(seg.id);
                        return (
                          <div
                            key={seg.id}
                            className="grid grid-cols-[1fr_140px_120px_120px] gap-xs px-md py-xs border-t border-greyscale-border items-center"
                          >
                            <span className="text-sm text-greyscale-text-title">
                              {seg.name}
                            </span>
                            <div className="flex items-center gap-xxs">
                              <span className="text-sm text-greyscale-text-body">
                                {seg.employeeCount}
                              </span>
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={seg.employeeCount === 0}
                                      onClick={() => {
                                        setViewingSegment({ company, segment: seg });
                                        setEmployeeSearch("");
                                        setView("segment-employees");
                                      }}
                                      className={seg.employeeCount === 0
                                        ? "text-greyscale-border cursor-not-allowed"
                                        : "text-greyscale-text-caption hover:text-greyscale-text-title"
                                      }
                                    >
                                      <Users size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="dark">View Employees</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div>
                              <Tag variant={inUse ? "success" : "drafted"}>
                                {inUse ? "In Use" : "Not in Use"}
                              </Tag>
                            </div>
                            <div className="flex items-center justify-start gap-xxxs">
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setViewingSegment({ company, segment: seg });
                                        setView("view-segment");
                                      }}
                                      className="text-greyscale-text-caption hover:text-greyscale-text-title h-8 w-8"
                                    >
                                      <Eye size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="dark">View</TooltipContent>
                                </Tooltip>
                                {!inUse && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setViewingSegment({ company, segment: seg });
                                          setView("edit-segment");
                                        }}
                                        className="text-greyscale-text-caption hover:text-greyscale-text-title h-8 w-8"
                                      >
                                        <Pencil size={14} />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent variant="dark">Edit</TooltipContent>
                                  </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={inUse}
                                      onClick={() =>
                                        deleteSegment(company.id, seg.id)
                                      }
                                      className={inUse
                                        ? "text-greyscale-border cursor-not-allowed h-8 w-8"
                                        : "text-greyscale-text-caption hover:text-red-500 h-8 w-8"
                                      }
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="dark">
                                    {inUse ? "Cannot delete — segment in use" : "Delete"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        );
                      })}

                      {company.segments.length === 0 && (
                        <div className="px-md py-sm border-t border-greyscale-border">
                          <p className="text-sm text-greyscale-text-caption">No segments yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- CREATE NEW SEGMENT ---------- */}
      {view === "create-segment" && (
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mb-md">
            <Button
              variant="link"
              className="hover:text-greyscale-text-title p-0 h-auto"
              onClick={() => setView("segments")}
            >
              Manage Segments
            </Button>
            <ChevronRight size={14} />
            <span className="text-greyscale-text-title font-semibold">Create New Segment</span>
          </div>

          <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
            Create new segment
          </h2>

          <div className="max-w-[850px]">
            {/* Select company */}
            <div className="mb-md">
              <p className="text-sm font-semibold text-greyscale-text-title mb-xxxs">
                Select company / franchise
              </p>
              <p className="text-sm text-greyscale-text-body mb-xxs">
                Choose which company this segment belongs to.
              </p>
              <Select
                value={createSegmentCompanyId}
                onValueChange={setCreateSegmentCompanyId}
              >
                <SelectTrigger className="w-full max-w-[400px]">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Segment builder - only show after company selected */}
            {createSegmentCompanyId && (
              <SegmentBuilder
                onSave={(name, _rules, estimatedCount) => {
                  const segId = `s-${name.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;
                  setCompanies((prev) =>
                    prev.map((c) =>
                      c.id === createSegmentCompanyId
                        ? {
                            ...c,
                            segments: [
                              ...c.segments,
                              { id: segId, name, employeeCount: estimatedCount },
                            ],
                          }
                        : c
                    )
                  );
                  setCreateSegmentCompanyId("");
                  setView("segments");
                }}
                onCancel={() => {
                  setCreateSegmentCompanyId("");
                  setView("segments");
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* ---------- EDIT SEGMENT ---------- */}
      {view === "edit-segment" && viewingSegment && (
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mb-md">
            <Button
              variant="link"
              className="hover:text-greyscale-text-title p-0 h-auto"
              onClick={() => { setView("segments"); setViewingSegment(null); }}
            >
              Manage Segments
            </Button>
            <ChevronRight size={14} />
            <span className="text-greyscale-text-title font-semibold">
              Edit: {viewingSegment.segment.name}
            </span>
          </div>

          <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
            Edit segment
          </h2>

          <div className="max-w-[850px]">
            {/* Company (read-only) */}
            <div className="mb-md">
              <p className="text-sm font-semibold text-greyscale-text-title mb-xxxs">
                Company / franchise
              </p>
              <p className="text-sm text-greyscale-text-caption">
                {viewingSegment.company.name}
              </p>
            </div>

            {/* Segment builder pre-filled */}
            <SegmentBuilder
              defaultName={viewingSegment.segment.name}
              defaultRules={[{
                id: "edit-rule-1",
                field: "job_title",
                operator: "contains",
                values: [viewingSegment.segment.name],
                valueType: "text",
              }]}
              saveLabel="Save Changes"
              onSave={(name, _rules, estimatedCount) => {
                setCompanies((prev) =>
                  prev.map((c) =>
                    c.id === viewingSegment.company.id
                      ? {
                          ...c,
                          segments: c.segments.map((s) =>
                            s.id === viewingSegment.segment.id
                              ? { ...s, name, employeeCount: estimatedCount }
                              : s
                          ),
                        }
                      : c
                  )
                );
                setViewingSegment(null);
                setView("segments");
              }}
              onCancel={() => {
                setViewingSegment(null);
                setView("segments");
              }}
            />
          </div>
        </div>
      )}

      {/* ---------- VIEW SEGMENT DETAIL ---------- */}
      {view === "view-segment" && viewingSegment && (() => {
        const inUse = segmentsInUse.has(viewingSegment.segment.id);
        return (
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mb-md">
            <Button
              variant="link"
              className="hover:text-greyscale-text-title p-0 h-auto"
              onClick={() => { setView("segments"); setViewingSegment(null); }}
            >
              Manage Segments
            </Button>
            <ChevronRight size={14} />
            <span className="text-greyscale-text-title font-semibold">
              {viewingSegment.segment.name}
            </span>
          </div>

          <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
            Segment details
          </h2>

          <div className="flex flex-col gap-lg max-w-[850px]">
            {/* Segment name */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Segment name</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">
                {viewingSegment.segment.name}
              </p>
            </div>

            {/* Description */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Description</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">
                All employees matching this segment in {viewingSegment.company.name}
              </p>
            </div>

            {/* Rules criteria */}
            <div>
              <h3 className="text-lg font-heading font-semibold text-greyscale-text-title mb-sm">
                Rules criteria
              </h3>
              <div className="grid grid-cols-3 gap-xs">
                <span className="text-sm font-semibold text-greyscale-text-title">Field</span>
                <span className="text-sm font-semibold text-greyscale-text-title">Rule</span>
                <span className="text-sm font-semibold text-greyscale-text-title">Value</span>
              </div>
              <div className="grid grid-cols-3 gap-xs mt-xxxs">
                <span className="text-sm text-greyscale-text-caption">Job Title</span>
                <span className="text-sm text-greyscale-text-caption">Includes</span>
                <span className="text-sm text-greyscale-text-caption">{viewingSegment.segment.name}</span>
              </div>
            </div>

            {/* Company */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Company</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">
                {viewingSegment.company.name}
              </p>
            </div>

            {/* Date created */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Date created</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">2025-12-15 04:16</p>
            </div>

            {/* Created by */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Created by (User)</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">Sarah Johnson</p>
            </div>

            {/* Last updated */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Last updated</p>
              <p className="text-sm text-greyscale-text-caption mt-xxxs">2026-02-10 11:32 by Mark Peters</p>
            </div>

            {/* Segment status */}
            <div>
              <p className="text-sm font-semibold text-greyscale-text-title">Segment status</p>
              {inUse ? (
                <>
                  <p className="text-sm text-greyscale-text-caption mt-xxxs mb-xxs">
                    This segment is currently active, scheduled, or drafted in a Multimessage announcement
                  </p>
                  <Tag variant="success">Currently in use</Tag>
                </>
              ) : (
                <>
                  <p className="text-sm text-greyscale-text-caption mt-xxxs mb-xxs">
                    This segment is not currently in use
                  </p>
                  <Tag variant="drafted">Not in use</Tag>
                </>
              )}
            </div>

            {/* Estimated match */}
            <div className="flex items-center gap-xs px-md py-sm bg-[#FFF4ED] rounded-md border border-[#FDDCBB]">
              <AlertCircle size={18} className="text-[#E5722E]" />
              <span className="text-sm font-semibold text-[#E5722E]">
                Estimated match: {viewingSegment.segment.employeeCount} Employees (at time of announcement creation)
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-greyscale-border mt-lg" />
          <div className="flex items-center justify-between py-lg">
            <Button variant="outline" onClick={() => { setView("segments"); setViewingSegment(null); }}>
              Go Back
            </Button>
          </div>
        </div>
        );
      })()}

      {/* ---------- SEGMENT EMPLOYEES LIST ---------- */}
      {view === "segment-employees" && viewingSegment && (() => {
        const employees = mockEmployeeNames[viewingSegment.segment.id] || [];
        const filtered = employees.filter((e) => {
          if (!employeeSearch) return true;
          const q = employeeSearch.toLowerCase();
          return e.surname.toLowerCase().includes(q) || e.firstName.toLowerCase().includes(q);
        });

        return (
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mb-md">
              <Button
                variant="link"
                className="hover:text-greyscale-text-title p-0 h-auto"
                onClick={() => { setView("segments"); setViewingSegment(null); }}
              >
                Manage Segments
              </Button>
              <ChevronRight size={14} />
              <Button
                variant="link"
                className="hover:text-greyscale-text-title p-0 h-auto"
                onClick={() => setView("segments")}
              >
                {viewingSegment.company.name}
              </Button>
              <ChevronRight size={14} />
              <span className="text-greyscale-text-title font-semibold">
                Employees ({filtered.length})
              </span>
            </div>

            <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-sm">
              {viewingSegment.segment.name} – Employees
            </h2>
            <p className="text-sm text-greyscale-text-body mb-md">
              {viewingSegment.company.name}
            </p>

            {/* Search */}
            <div className="relative mb-md max-w-[500px]">
              <Search size={16} className="absolute left-xs top-1/2 -translate-y-1/2 text-greyscale-text-caption" />
              <InputField
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder="Search employee"
                className="w-full pl-lg"
              />
            </div>

            {/* Employee table */}
            <DataTable
              columns={employeeColumns}
              data={filtered}
              showPagination={false}
              showToolbar={false}
            />

            {/* Footer */}
            <div className="border-t border-greyscale-border mt-lg" />
            <div className="flex items-center justify-between py-lg">
              <Button variant="outline" onClick={() => { setView("segments"); setViewingSegment(null); }}>
                Go Back
              </Button>
            </div>
          </div>
        );
      })()}

      {/* ---------- VIEW MESSAGE DETAIL ---------- */}
      {view === "view" && viewingMessage && (() => {
        const companiesList = messageCompanies[viewingMessage.id] || [];
        const statusMap: Record<string, string> = {
          LIVE: "success",
          DRAFT: "drafted",
          SCHEDULED: "processing",
          EXPIRED: "failed",
        };

        return (
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mb-md">
              <Button
                variant="link"
                className="hover:text-greyscale-text-title p-0 h-auto"
                onClick={() => { setView("list"); setViewingMessage(null); }}
              >
                Multimessaging
              </Button>
              <ChevronRight size={14} />
              <span className="text-greyscale-text-title font-semibold">
                {viewingMessage.title}
              </span>
            </div>

            <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
              Message details
            </h2>

            <div className="flex flex-col gap-lg max-w-[850px]">
              {/* Title */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Title</p>
                <p className="text-sm text-greyscale-text-caption mt-xxxs">{viewingMessage.title}</p>
              </div>

              {/* Template */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Template</p>
                <p className="text-sm text-greyscale-text-caption mt-xxxs">{viewingMessage.template || "—"}</p>
              </div>

              {/* Message */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Message</p>
                <p className="text-sm text-greyscale-text-caption mt-xxxs">{viewingMessage.body || "—"}</p>
              </div>

              {/* Companies sent to */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Companies sent to</p>
                <span className="text-sm text-greyscale-text-caption mt-xxxs">
                  {viewingMessage.companiesSent} {viewingMessage.companiesSent === 1 ? "company" : "companies"}
                </span>
                {companiesList.length > 0 && (
                  <div className="flex flex-wrap gap-xxs mt-xxs">
                    {companiesList.map((name) => (
                      <DismissibleTag key={name} variant="pink">
                        {name}
                      </DismissibleTag>
                    ))}
                  </div>
                )}
              </div>

              {/* Total recipients */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Total recipients</p>
                <div className="flex items-center gap-xxs mt-xxxs">
                  <Users size={14} className="text-greyscale-text-caption" />
                  <span className="text-sm text-greyscale-text-caption">
                    {viewingMessage.totalRecipients} employees
                  </span>
                </div>
              </div>

              {/* Sent / Close dates */}
              <div className="grid grid-cols-2 gap-lg">
                <div>
                  <p className="text-sm font-semibold text-greyscale-text-title">Sent date</p>
                  <p className="text-sm text-greyscale-text-caption mt-xxxs">
                    {viewingMessage.sentDate || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-greyscale-text-title">Close date</p>
                  <p className="text-sm text-greyscale-text-caption mt-xxxs">
                    {viewingMessage.closeDate || "—"}
                  </p>
                </div>
              </div>

              {/* Sent by */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Sent by</p>
                <p className="text-sm text-greyscale-text-caption mt-xxxs">{viewingMessage.sentBy}</p>
              </div>

              {/* Attachment */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Attachment</p>
                <div className="flex items-center gap-xxs mt-xxxs">
                  <Paperclip size={14} className="text-greyscale-text-caption" />
                  <span className="text-sm text-greyscale-text-caption">
                    {viewingMessage.hasAttachment ? "Yes" : "None"}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div>
                <p className="text-sm font-semibold text-greyscale-text-title">Status</p>
                <div className="mt-xxxs">
                  <Tag variant={statusMap[viewingMessage.status] as any}>
                    {viewingMessage.status}
                  </Tag>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-greyscale-border mt-lg" />
            <div className="flex items-center justify-between py-lg">
              <Button variant="outline" onClick={() => { setView("list"); setViewingMessage(null); }}>
                Go Back
              </Button>
              {viewingMessage.status === "LIVE" && (
                <Button
                  className="bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => setShowCloseModal(true)}
                >
                  Close Announcement
                </Button>
              )}
            </div>
          </div>
        );
      })()}

      {/* ---------- AUDIT LOG VIEW ---------- */}
      {view === "audit" && (
        <div>
          <div className="flex items-center justify-between mb-md">
            <p className="text-base font-semibold text-greyscale-text-title">
              History Log
            </p>
            <Button variant="outline" leftIcon={<Download size={16} />}>
              Download CSV
            </Button>
          </div>

          <DataTable
            columns={auditColumns}
            data={auditLog}
            showPagination={false}
            showToolbar={false}
          />

          <div className="mt-md">
            <Button variant="outline" onClick={() => setView("list")}>
              Back to Messages
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Dialog open={!!deletingMessage} onOpenChange={(open) => { if (!open) setDeletingMessage(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete draft</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deletingMessage?.title}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={() => setDeletingMessage(null)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close announcement confirmation modal */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close announcement</DialogTitle>
            <DialogDescription>
              Are you sure you want to close this announcement? It will no longer be visible to employees.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={() => {
                const targetId = viewingMessage?.id || editingMessage?.id;
                if (targetId) {
                  setMessageList((prev) =>
                    prev.map((m) =>
                      m.id === targetId ? { ...m, status: "EXPIRED" } : m
                    )
                  );
                }
                setShowCloseModal(false);
                setEditingMessage(null);
                setViewingMessage(null);
                setView("list");
              }}
            >
              Close Announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
